import React, { FC, useContext, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useDispatch, useSelector } from 'react-redux';
import { BasketPaymentDetailsProps } from '@components/v2/BasketPaymentDetails';
import { usePrices } from '@hooks/usePriceCalculator';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST, FeatureSwitchContext } from '../../../context/featureSwitchApp';
import FinanceWidget from '@components/FinanceWidget';
import { faqsTemplateId } from '@components/FAQs';
import { BUDGET_TYPE_CASH, BUDGET_TYPE_FINANCE, BUDGET_TYPE_RENT, TBudgetType } from '../../../services';
import FilterDuck from '../../../redux/filters/filter.duck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import Spinner from '@components/Spinner';
import { theme } from '../../../styles/theme';
import { BASKET_STEPS, LEASYS_PROVIDER_TYPE, ModalVersion, PxVersion } from '../../../constants/main';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { useJourneyType } from '@hooks/useJourneyType';
import { updateBasketSteps } from '@utils/Car.utils';
import { Link } from '@components/v2/link';
import NeedHandSection from '@components/OfferListItem/NeedHandSection';
import { PaymentJourneyTypes } from '../../../partExchange/interfaces/Default';
import { useGTM } from '@hooks/useGTM';
import BasketCashPaymentSelection from '@components/v2/BasketCashPaymentSelection';
import BasketRentPaymentSelection from '@components/v2/BasketRentPaymentSelection';
import BasketFinancePaymentSelection from '@components/v2/BasketFinancePaymentSelection';
import FinanceWidgetDuck from '../../../redux/financeWidget/financeWidget.duck';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';
import { Redux } from '../../../redux/redux.interface';
import ConfiguratorDuck from '../../../redux/configurator/configurator.duck';
import { PX_STATUSES_APPROVED, PX_STATUSES_WAITING } from '../../../partExchange/constants/config';
import { useExperiment } from '@hooks/useExperiment';
import { PosthogTrigger } from '../../../constants/enums';

const BasketPaymentDetailsTemplate: FC<BasketPaymentDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { pushTrigger, getPageNameByUrl } = useExperiment();
    const context = useContext(FeatureSwitchContext);
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { leasysQuote, defaultLeasysQuote } = useFinanceWidgetDuck();

    const { pushToDataLayer } = useGTM();

    const [renderWidget, setRenderWidget] = useState<() => void>();
    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);
    const hasLeasysSelected = useSelector((state) => FilterDuck.getOwnState(state).hasLeasysSelected);
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);

    const { cash, finance } = usePrices();

    const [isOpen, setIsOpen] = useState(true);

    const handleFinanceWidgetBtnClick = () => {
        renderWidget && renderWidget();
    };

    const registerRenderCb = (cb: () => void) => {
        setRenderWidget(() => cb);
    };

    const { totalPrice: cashPrice } = cash;
    const { totalPrice: monthlyPrice } = finance;

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);
    const isLeasysEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_LEASYS_ENABLED);
    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);
    const removePxOnPaymentChange = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_REMOVE_PX_ON_PAYMENT_CHANGE);
    const isAutomaticOpeningFinanceWidgetEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_AUTOMATIC_OPENING_FINANCE_WIDGET_ON_BASKET_PAGE_ENABLED
    );

    const scrollToFAQs = () => {
        const element = document.getElementById(faqsTemplateId);

        return element.scrollIntoView();
    };

    const { paymentJourneyType, isRent } = useJourneyType();
    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};
    const selectPaymentMethod = async (selectedPaymentMethod: string) => {
        const previousPaymentJourney = paymentJourneyType;
        const hasPreviousSelectedJourney = hasSelectedPaymentJourney;
        const wasPreviousJourneyRenting = isRent;

        pushTrigger(PosthogTrigger.PAYMENT, getPageNameByUrl(), 'change', null, null, selectedPaymentMethod);

        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Content',
            eventAction: 'Filter::PriceView',
            eventLabel: selectedPaymentMethod,
        });

        if (selectedPaymentMethod === BUDGET_TYPE_RENT || selectedPaymentMethod === BUDGET_TYPE_CASH) {
            dispatch(FinanceWidgetDuck.setFinanceQuote(null));
        }

        if (selectedPaymentMethod === BUDGET_TYPE_FINANCE || selectedPaymentMethod === BUDGET_TYPE_CASH) {
            dispatch(FinanceWidgetDuck.setLeasysQuote(defaultLeasysQuote));
        }

        dispatch(FilterDuck.hasLeasysSelected(BUDGET_TYPE_RENT === selectedPaymentMethod));

        if (!hasSelectedPaymentJourney && paymentJourneyType === selectedPaymentMethod) {
            dispatch(UIDuck.setLoading(5));
            dispatch(
                FilterDuck.changeBudgetType(
                    BUDGET_TYPE_CASH === selectedPaymentMethod ? BUDGET_TYPE_CASH : BUDGET_TYPE_FINANCE
                )
            );

            // update deal that we infact selected
            await updateBasketSteps(dispatch, currentDeal, BASKET_STEPS.PAYMENT, selectedPaymentMethod);
            dispatch(UIDuck.setLoading(-5));
        } else {
            dispatch(
                FilterDuck.changeBudgetType(
                    BUDGET_TYPE_CASH === selectedPaymentMethod ? BUDGET_TYPE_CASH : BUDGET_TYPE_FINANCE
                )
            );
        }

        if (
            isAutomaticOpeningFinanceWidgetEnabled &&
            selectedPaymentMethod === PaymentJourneyTypes.FINANCE &&
            (!hasPreviousSelectedJourney ||
                previousPaymentJourney === PaymentJourneyTypes.CASH ||
                wasPreviousJourneyRenting)
        ) {
            handleFinanceWidgetBtnClick();
        }
    };

    useEffect(() => {
        if (!monthlyPrice && (!isLeasysEnabled || (isLeasysEnabled && !leasysQuote))) {
            selectPaymentMethod(BUDGET_TYPE_CASH);
        }
    }, []);

    const isCashSelected =
        !hasLeasysSelected &&
        currentDeal?.extraFields?.providerType != LEASYS_PROVIDER_TYPE &&
        (hasSelectedPaymentJourney || (!monthlyPrice && isLeasysEnabled && !leasysQuote)) &&
        paymentJourneyType === BUDGET_TYPE_CASH;

    const isLeasysSelected = hasLeasysSelected || currentDeal?.extraFields?.providerType === LEASYS_PROVIDER_TYPE;

    const isFinanceSelected =
        currentDeal?.extraFields?.providerType != LEASYS_PROVIDER_TYPE &&
        !hasLeasysSelected &&
        hasSelectedPaymentJourney &&
        paymentJourneyType === BUDGET_TYPE_FINANCE;

    const handleCheckPxForCashAndFinancePaymentMethod = (budgetType: TBudgetType) => {
        if (
            removePxOnPaymentChange &&
            pxVersion === PxVersion.v2 &&
            [...PX_STATUSES_APPROVED, PX_STATUSES_WAITING].includes(autobizPxState?.statusId) &&
            paymentJourneyType !== budgetType
        ) {
            dispatch(
                UIDuck.openModal({
                    data: {
                        onContinueClick: () => selectPaymentMethod(budgetType),
                        description: t('modal.completePx.modify.description'),
                        title: t('modal.completePx.modify.title'),
                        backButton: t('modal.completePx.modify.button.close'),
                        continueButton: t('modal.completePx.modify.button.continue'),
                        withoutDelete: true,
                    },
                    modalType: UIDuck.MODAL_TYPES.INCOMPLETE_PX,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );

            return false;
        }

        return true;
    };

    const handleCheckPxForLeasysPaymentMethod = () => {
        if (pxVersion === PxVersion.v2 && autobizPxState?.statusId) {
            dispatch(
                UIDuck.openModal({
                    data: {
                        onContinueClick: () => selectPaymentMethod(BUDGET_TYPE_RENT),
                        description: t('modal.incompletePx.modify.description'),
                        title: t('modal.incompletePx.modify.title'),
                        backButton: t('modal.incompletePx.modify.button.close'),
                        continueButton: t('modal.incompletePx.modify.button.continue'),
                        withoutDelete: true,
                    },
                    modalType: UIDuck.MODAL_TYPES.INCOMPLETE_PX,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );

            return false;
        }

        return true;
    };

    const isDemoVehicle = carDetails?.mileage && carDetails?.registrationDate;

    return (
        <div id="payments" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.payment.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                {!isDemoVehicle && (
                    <div className="description">
                        <span>{t('basket.payment.description')}</span>
                        <Link
                            withParent={false}
                            isUnderline
                            txt={t('basket.payment.description.link')}
                            onClick={scrollToFAQs}
                        >
                            <button className="link" onClick={scrollToFAQs}>
                                {t('basket.payment.description.link')}
                            </button>
                        </Link>
                    </div>
                )}
                <BasketCashPaymentSelection
                    isSelected={isCashSelected}
                    onClick={() => {
                        const hasInActivePxForThisPaymentMethod =
                            handleCheckPxForCashAndFinancePaymentMethod(BUDGET_TYPE_CASH);
                        if (hasInActivePxForThisPaymentMethod) selectPaymentMethod(BUDGET_TYPE_CASH);
                    }}
                />
                {isLeasysEnabled && leasysQuote && (
                    <BasketRentPaymentSelection
                        isSelected={isLeasysSelected}
                        onClick={() => {
                            const hasInActivePxForThisPaymentMethod = handleCheckPxForLeasysPaymentMethod();
                            if (hasInActivePxForThisPaymentMethod) selectPaymentMethod(BUDGET_TYPE_RENT);
                        }}
                    />
                )}
                {monthlyPrice && (
                    <BasketFinancePaymentSelection
                        isSelected={isFinanceSelected}
                        onClick={() => {
                            const hasInActivePxForThisPaymentMethod =
                                handleCheckPxForCashAndFinancePaymentMethod(BUDGET_TYPE_FINANCE);
                            if (hasInActivePxForThisPaymentMethod) selectPaymentMethod(BUDGET_TYPE_FINANCE);
                        }}
                        handleFinanceWidgetBtnClick={handleFinanceWidgetBtnClick}
                    />
                )}
                {isLoading && <Spinner size={25} border={3} color={theme.colors.black} />}
                <NeedHandSection isSidebar isBasket isInlineSnippet />
            </Collapsible>
            {!isFinanceWidgetDisabled && <FinanceWidget registerRenderCb={registerRenderCb} />}
        </div>
    );
};

export default BasketPaymentDetailsTemplate;
